import React from 'react';
import type { PlayerRef } from '@remotion/player';

interface PreviewControlsProps {
  playerRef: React.RefObject<PlayerRef | null>;
  isPlaying: boolean;
  currentFrame: number;
  durationInFrames: number;
  fps: number;
}

export function PreviewControls({
  playerRef,
  isPlaying,
  currentFrame,
  durationInFrames,
  fps,
}: PreviewControlsProps) {
  const progress = durationInFrames > 0 ? currentFrame / durationInFrames : 0;
  const currentTime = formatTime(currentFrame / fps);
  const totalTime = formatTime(durationInFrames / fps);

  function handlePlayPause() {
    const p = playerRef.current;
    if (!p) return;
    if (isPlaying) p.pause();
    else p.play();
  }

  function handleRestart() {
    const p = playerRef.current;
    if (!p) return;
    p.seekTo(0);
    p.play();
  }

  function handleScrub(e: React.ChangeEvent<HTMLInputElement>) {
    const p = playerRef.current;
    if (!p) return;
    p.seekTo(Math.round(Number(e.target.value) * durationInFrames));
  }

  return (
    <div className="flex items-center gap-3 px-4 py-2 bg-zinc-900 border-t border-zinc-700 shrink-0">
      {/* Restart */}
      <button
        type="button"
        onClick={handleRestart}
        className="text-zinc-400 hover:text-white transition-colors"
        title="Restart"
        aria-label="Restart"
      >
        <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2} aria-hidden="true">
          <path strokeLinecap="round" strokeLinejoin="round" d="M3 10h10a8 8 0 018 8v2M3 10l6 6m-6-6l6-6" />
        </svg>
      </button>

      {/* Play/Pause */}
      <button
        type="button"
        onClick={handlePlayPause}
        className="w-7 h-7 rounded-full bg-blue-600 hover:bg-blue-500 flex items-center justify-center transition-colors shrink-0"
        aria-label={isPlaying ? 'Pause' : 'Play'}
      >
        {isPlaying ? (
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <rect x="6" y="4" width="4" height="16" />
            <rect x="14" y="4" width="4" height="16" />
          </svg>
        ) : (
          <svg className="w-3 h-3 text-white" fill="currentColor" viewBox="0 0 24 24" aria-hidden="true">
            <path d="M8 5v14l11-7z" />
          </svg>
        )}
      </button>

      {/* Time */}
      <span className="text-xs font-mono text-zinc-400 shrink-0 w-20">
        {currentTime} / {totalTime}
      </span>

      {/* Scrubber */}
      <input
        type="range"
        min={0}
        max={1}
        step={0.001}
        value={progress}
        onChange={handleScrub}
        className="flex-1 h-1 accent-blue-500 cursor-pointer"
        aria-label="Seek"
      />

      {/* Frame counter */}
      <span className="text-[10px] font-mono text-zinc-600 shrink-0">
        f{currentFrame}
      </span>
    </div>
  );
}

function formatTime(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = Math.floor(seconds % 60);
  return `${m}:${String(s).padStart(2, '0')}`;
}
